import Input from '@/components/Input'
import { useRef, useEffect } from 'react'
import { useRecoilState } from 'recoil'
import { videoProgressState, currentAnnotationState } from '@/store'
import dynamic from 'next/dynamic'
import { mdParser } from '@/utils/index'
import OcrButton from '@/components/AnnotationsView/tabs/OcrButton'
import TranscriptButton from '@/components/AnnotationsView/tabs/TranscriptButton'
import AutoTranscriptButton from '@/components/AnnotationsView/tabs/AutoTranscriptButton'
import Button from '@/components/Button'
import StartTimeButton from '@/components/AnnotationsView/tabs/StartTimeButton'
import StartTimePicker from '@/components/AnnotationsView/tabs/StartTimePicker'
import { X, Check } from 'react-feather'
import { useDeepCompareEffectNoCheck } from 'use-deep-compare-effect'

const MdEditor = dynamic(
  async () => {
    const { default: ImportedEditor } = await import(
      'react-markdown-editor-lite'
    )

    function EditorWithRef({ forwardedRef, ...props }) {
      return <ImportedEditor ref={forwardedRef} {...props} />
    }

    return EditorWithRef
  },
  {
    ssr: false,
  }
)

export default function EditorTab({
  video,
  isDemo,
  setCurrentView,
  title,
  content,
  onTitleChange,
  onContentChange,
  setNewContent,
  resetState,
  onUpdate,
  onCreate,
  newAnnotationStart,
  setNewAnnotationStart,
  isLoading,
}) {
  const [progress] = useRecoilState(videoProgressState)
  const mdEditorRef = useRef(null)
  const [currentAnnotation] = useRecoilState(currentAnnotationState)
  const showTranscript = video.platform === 'youtube'
  const showTimePicker =
    newAnnotationStart !== null && newAnnotationStart !== undefined

  useEffect(() => {
    if (mdEditorRef.current) {
      const elements = document.querySelectorAll('input[type=file]')
      elements.forEach((el) => (el.tabIndex = -1))
    }
  }, [mdEditorRef.current])

  useDeepCompareEffectNoCheck(() => {
    function onShortcutSubmit(e) {
      const isEnter = e.keyCode === 13
      const isSubmitKey = e.metaKey || e.ctrlKey

      if (isEnter && isSubmitKey) {
        if (currentAnnotation) {
          onUpdate()
        } else {
          onCreate()
        }
      }
    }

    if (mdEditorRef.current) {
      mdEditorRef.current.on('keydown', onShortcutSubmit)
    }

    return () => {
      if (mdEditorRef.current) {
        mdEditorRef.current.off('keydown', onShortcutSubmit)
      }
    }
  }, [currentAnnotation, mdEditorRef.current, onUpdate, onCreate])

  function onInputSubmit(e) {
    const isEnter = e.keyCode === 13
    const isSubmitKey = e.metaKey || e.ctrlKey

    if (isEnter) {
      e.preventDefault()
      e.stopPropagation()
    }

    if (isEnter && isSubmitKey) {
      if (currentAnnotation) {
        onUpdate()
      } else {
        onCreate()
      }
    }
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-row mb-3 mt-1">
        <form
          className="w-full"
          style={{ padding: '1px' }}
          onKeyDown={onInputSubmit}
        >
          <Input
            value={title}
            onChange={({ target }) => onTitleChange(target.value)}
            placeholder="Title"
            type="text"
            style={{ minWidth: '200px' }}
          />
        </form>

        <OcrButton {...{ content, onContentChange, className: 'ml-3' }} />

        {showTranscript && (
          <AutoTranscriptButton
            {...{
              content,
              setNewContent,
              videoUrl: video.url,
              isDemo,
              className: 'ml-3',
            }}
          />
        )}

        {showTranscript && (
          <TranscriptButton
            {...{
              content,
              onContentChange,
              videoUrl: video.url,
              setCurrentView,
              isDemo,
              className: 'ml-3',
            }}
          />
        )}

        {showTimePicker && (
          <>
            <StartTimeButton
              {...{
                setNewAnnotationStart,
                progress,
                isDemo,
                className: 'ml-3',
              }}
            />

            <StartTimePicker
              {...{
                newAnnotationStart,
                setNewAnnotationStart,
                duration: video.duration,
                isDemo,
              }}
            />
          </>
        )}

        <Divider />

        {(title || content) && (
          <Button
            onClick={resetState}
            className="ml-3"
            tooltipContent="Cancel"
            name="cancel"
            tabIndex={-1}
          >
            <X className="h-5 w-5" />
          </Button>
        )}

        {currentAnnotation && (
          <Button
            onClick={onUpdate}
            className="ml-3"
            tooltipContent="Update"
            isLoading={isLoading}
            name="update"
            tabIndex={-1}
          >
            <Check className="h-5 w-5" />
          </Button>
        )}

        {!currentAnnotation && (
          <Button
            onClick={onCreate}
            className="ml-3 save-button"
            tooltipContent="Create"
            isLoading={isLoading}
            name="create"
            tabIndex={-1}
          >
            <Check className="h-5 w-5" />
          </Button>
        )}
      </div>

      <MdEditor
        forwardedRef={mdEditorRef}
        value={content}
        style={{ flex: 1 }}
        config={{
          view: {
            menu: true,
            md: true,
            html: false,
          },
          canView: {
            menu: true,
            md: true,
            html: true,
            fullScreen: true,
            hideMenu: true,
          },
        }}
        onChange={onContentChange}
        renderHTML={(text) => mdParser.render(text)}
      />
    </div>
  )
}

function Divider() {
  return <div className="h-full border-l border-gray-300 ml-3" />
}
